import { ApolloClient, ApolloLink, HttpLink, Operation, split, fromPromise } from '@apollo/client';
import { InMemoryCache, NormalizedCacheObject } from '@apollo/client/cache';
import { getMainDefinition, relayStylePagination } from '@apollo/client/utilities';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import rollbar from '@assets/util/rollbar';
import { createConsumer, logger } from '@rails/actioncable';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';

let rootPath;
const ENV = process.env.NODE_ENV;

if (ENV === 'development') {
  rootPath = 'http://localhost:3000';
} else if (ENV === 'test') {
  rootPath = 'http://' + window.location.hostname + ':' + window.location.port;
} else if (ENV === 'circle') {
  if (window.location.href) {
    if (window.location.href.indexOf('staging.sumzero.com') > -1) {
      rootPath = 'https://staging.sumzero.com';
    } else if (window.location.href.indexOf('sumzero.com') > -1) {
      rootPath = 'https://www.sumzero.com';
    } else {
      rootPath = 'http://' + window.location.hostname + ':' + window.location.port;
    }
  } else {
    rootPath = 'http://' + window.location.hostname + ':' + window.location.port;
  }
} else {
  rootPath = window.location.origin ? window.location.origin : 'https://www.sumzero.com';
}

let csrfToken = '';
const authLink = setContext((_, { headers }) => {
  if (!csrfToken) {
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    csrfToken = metaTag?.getAttribute('content') ?? '';
  }

  return {
    headers: {
      ...headers,
      'X-CSRF-TOKEN': csrfToken
    }
  };
});

let refreshPromise: Promise<void> | null = null;
const refreshCSRFToken = (): Promise<void> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = fetch(`${rootPath}/csrf_token`, { credentials: 'same-origin' })
    .then(res => {
      if (!res.ok) throw new Error('CSRF refresh failed');
      return res.json();
    })
    .then(data => {
      csrfToken = data.token;
      return;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const mutedErrors = [
  'TypeError: Type error',
  'TypeError: Load failed',
  'TypeError: NetworkError when attempting to fetch resource.',
  'TypeError: Failed to fetch',
  'TypeError: Network request failed',
  'Unexpected token N in JSON at position 0',
  'TypeError: Could not connect to the server.',
  'TypeError: cancelled',
  'Error: Response not successful: Received status code 401',
  'TypeError: The network connection was lost.',
  'ServerError: Response not successful: Received status code 422'
];

const mutedGraphQLErrors = [
  'Not authenticated',
  'Your thesis does not meet the required',
  'Could not process watchlist tag relationship',
  'Name has already been taken',
  'Invalid password',
  'You are not part of this conversation',
  'User does not have Messages/manage permission',
  'was provided invalid value'
];

const shouldIgnore = (message: string): boolean =>
  mutedGraphQLErrors.find(err => message.includes(err)) ? true : false;

const errorHandler = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (ENV === 'circle') return;

  if (networkError) {
    const statusCode = 'statusCode' in networkError ? networkError.statusCode : null;

    if (statusCode === 401) {
      window.location.href = '/login';
      return;
    }

    if (statusCode === 422) {
      return fromPromise(refreshCSRFToken()).flatMap(() => forward(operation));
    }

    // eslint-disable-next-line no-console
    console.log(`[Network error]: ${networkError}, OperationName: ${operation.operationName}`);
    // Mutes failed to fetch errors
    if (mutedErrors.indexOf(String(networkError)) === -1) {
      rollbar.error(
        `[Apollo GraphQL network error] ${networkError}, OperationName: ${operation.operationName}`,
        { error: networkError, operation: operation }
      );
    }
  }

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) =>
      // eslint-disable-next-line no-console
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, OperationName: ${operation.operationName}`
      )
    );
    graphQLErrors
      .filter(({ message }) => !shouldIgnore(message))
      .forEach(error =>
        rollbar.error(
          `[Apollo GraphQL error] Message: ${error.message}, OperationName: ${operation.operationName}`,
          { error, operation }
        )
      );
  }

  return;
});

logger.enabled = ENV === 'development';

const cable = createConsumer();

const uploadLink = createUploadLink({
  uri: `${rootPath}/queries`,
  credentials: 'same-origin'
});

const batchHttpLink = new BatchHttpLink({
  uri: `${rootPath}/queries`,
  credentials: 'same-origin'
});

const httpLink = new HttpLink({
  uri: `${rootPath}/queries`,
  credentials: 'same-origin'
});

const isIdeaPositionsPricingOperation = ({ query }: Operation): boolean => {
  const definition = getMainDefinition(query);
  return (
    definition.kind === 'OperationDefinition' && definition.name?.value === 'IdeaPositionsPricing'
  );
};

const hasSubscriptionOperation = ({ query }: Operation): boolean => {
  const definition = getMainDefinition(query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
};

const hasFileUploadOperation = (operation: Operation): boolean =>
  extractFiles(operation, isExtractableFile).files.size > 0;

const generateClient = (): ApolloClient<NormalizedCacheObject> => {
  return new ApolloClient({
    devtools: {
      enabled: ENV === 'development'
    },
    link: ApolloLink.from([
      errorHandler,
      authLink,
      split(
        hasSubscriptionOperation,
        new ActionCableLink({ cable }),
        split(
          hasFileUploadOperation,
          uploadLink,
          split(isIdeaPositionsPricingOperation, httpLink, batchHttpLink)
        )
      )
    ]),
    cache: new InMemoryCache({
      possibleTypes: {
        FeedItem: [
          'AdminPost',
          'AllocatorProfile',
          'Comment',
          'CorporateAction',
          'EmployerProfile',
          'EmployerProfileUpdate',
          'FeaturedItem',
          'Idea',
          'IdeaUpdate',
          'JobPosting',
          'Post',
          'Rank',
          'WhitePaper'
        ]
      },
      typePolicies: {
        Query: {
          fields: {
            feedItemComments: relayStylePagination(),
            feedItems: relayStylePagination(['feedFilter']),
            postsIndexList: relayStylePagination(['searchParams']),
            threadMessages: relayStylePagination(),
            recommendedUsersToFollow: relayStylePagination(),
            currentUser: { merge: true },
            currentRanksForCategory: relayStylePagination(['category'])
          }
        },
        Chat: {
          fields: {
            chatMessages: relayStylePagination()
          }
        },
        Idea: {
          fields: {
            ideaViews: relayStylePagination()
          }
        },
        IdeaUpdate: {
          fields: {
            comments: relayStylePagination()
          }
        },
        Post: {
          fields: {
            comments: relayStylePagination()
          }
        },
        User: {
          fields: {
            publishedIdeas: relayStylePagination(),
            ratings: relayStylePagination(),
            viewerIntroRequests: relayStylePagination()
          }
        }
      }
    })
  });
};

export default generateClient;
